/**
 * Release Keyword Matching Utilities
 * 
 * Utilities for matching keywords in release names and descriptions
 * to categorize releases into appropriate swimlanes.
 */

import { ReleaseCategory, RELEASE_KEYWORDS } from '../types/release'

// ============================================================================
// KEYWORD MATCHING FUNCTIONS
// ============================================================================

/**
 * Normalize text for keyword matching
 * Removes special characters, normalizes whitespace, converts to lowercase
 */
export function normalizeText(text: string): string {
  if (!text || typeof text !== 'string') {
    return ''
  }
  
  return text
    .toLowerCase()
    .trim()
    // Replace multiple whitespace with single space
    .replace(/\s+/g, ' ')
    // Remove common punctuation that might interfere with matching
    .replace(/[.,;:!?()[\]{}'"]/g, ' ')
    // Clean up extra spaces
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Check if a keyword matches within text using case-insensitive matching
 * Supports both exact word matching and partial matching
 */
export function matchesKeyword(text: string, keyword: string, options: {
  exactWord?: boolean
  caseSensitive?: boolean
} = {}): boolean {
  const { exactWord = false, caseSensitive = false } = options
  
  if (!text || !keyword) {
    return false
  }
  
  const searchText = caseSensitive ? text : text.toLowerCase()
  const searchKeyword = caseSensitive ? keyword : keyword.toLowerCase()
  
  if (exactWord) {
    // Use word boundaries for exact word matching
    const wordRegex = new RegExp(`\\b${escapeRegExp(searchKeyword)}\\b`, caseSensitive ? 'g' : 'gi')
    return wordRegex.test(searchText)
  } else {
    // Simple substring matching
    return searchText.includes(searchKeyword)
  }
}

/**
 * Escape special regex characters
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Find all matching keywords in text for a given category
 */
export function findMatchingKeywords(
  text: string, 
  category: ReleaseCategory,
  options: {
    exactWord?: boolean
    caseSensitive?: boolean
  } = {}
): string[] {
  const keywords = RELEASE_KEYWORDS[category]
  const normalizedText = normalizeText(text)
  const matchedKeywords: string[] = []
  
  for (const keyword of keywords) {
    if (matchesKeyword(normalizedText, keyword, options)) {
      matchedKeywords.push(keyword)
    }
  }
  
  return matchedKeywords
}

/**
 * Find all matching keywords across all categories
 */
export function findAllMatchingKeywords(
  text: string,
  options: {
    exactWord?: boolean
    caseSensitive?: boolean
  } = {}
): Record<ReleaseCategory, string[]> {
  const results: Record<ReleaseCategory, string[]> = {
    [ReleaseCategory.SOFTWARE]: [],
    [ReleaseCategory.CLOUD]: [],
    [ReleaseCategory.AZURE]: [],
    [ReleaseCategory.OSS]: []
  }
  
  for (const category of Object.values(ReleaseCategory)) {
    results[category] = findMatchingKeywords(text, category, options)
  }
  
  return results
}

/**
 * Calculate match confidence score based on number and specificity of matches
 */
export function calculateMatchConfidence(
  matchedKeywords: string[],
  totalKeywords: number,
  textLength: number
): number {
  if (matchedKeywords.length === 0) {
    return 0
  }
  
  // Base confidence from match ratio
  const matchRatio = matchedKeywords.length / totalKeywords
  let confidence = Math.min(matchRatio * 0.7, 0.7) // Max 70% from match ratio
  
  // Bonus for multiple matches
  if (matchedKeywords.length > 1) {
    confidence += 0.1
  }
  
  // Bonus for longer, more specific keywords
  const avgKeywordLength = matchedKeywords.reduce((sum, kw) => sum + kw.length, 0) / matchedKeywords.length
  if (avgKeywordLength > 4) {
    confidence += 0.1
  }
  
  // Penalty for very short text (might be false positive)
  if (textLength < 10) {
    confidence *= 0.8
  }
  
  return Math.min(confidence, 1.0)
}

/**
 * Advanced keyword matching with context awareness
 */
export function matchKeywordsWithContext(
  text: string,
  category: ReleaseCategory,
  options: {
    exactWord?: boolean
    caseSensitive?: boolean
    contextWindow?: number
    boostFactors?: Record<string, number>
  } = {}
): {
  matches: string[]
  confidence: number
  contexts: Array<{ keyword: string; context: string; position: number }>
} {
  const { contextWindow = 20, boostFactors = {} } = options
  const normalizedText = normalizeText(text)
  const keywords = RELEASE_KEYWORDS[category]
  
  const matches: string[] = []
  const contexts: Array<{ keyword: string; context: string; position: number }> = []
  
  for (const keyword of keywords) {
    if (matchesKeyword(normalizedText, keyword, options)) {
      matches.push(keyword)
      
      // Extract context around the match
      const position = normalizedText.indexOf(keyword.toLowerCase())
      if (position !== -1) {
        const start = Math.max(0, position - contextWindow)
        const end = Math.min(normalizedText.length, position + keyword.length + contextWindow)
        const context = normalizedText.substring(start, end)
        
        contexts.push({ keyword, context, position })
      }
    }
  }
  
  // Calculate confidence with boost factors
  let confidence = calculateMatchConfidence(matches, keywords.length, text.length)
  
  // Apply boost factors for specific keywords
  for (const match of matches) {
    if (boostFactors[match]) {
      confidence = Math.min(confidence * boostFactors[match], 1.0)
    }
  }
  
  return { matches, confidence, contexts }
}

/**
 * Batch keyword matching for multiple texts
 */
export function batchMatchKeywords(
  texts: string[],
  category: ReleaseCategory,
  options: {
    exactWord?: boolean
    caseSensitive?: boolean
    minConfidence?: number
  } = {}
): Array<{
  text: string
  matches: string[]
  confidence: number
  isMatch: boolean
}> {
  const { minConfidence = 0.3 } = options
  
  return texts.map(text => {
    const matches = findMatchingKeywords(text, category, options)
    const confidence = calculateMatchConfidence(matches, RELEASE_KEYWORDS[category].length, text.length)
    const isMatch = confidence >= minConfidence
    
    return { text, matches, confidence, isMatch }
  })
}

// ============================================================================
// DEBUGGING AND TESTING UTILITIES
// ============================================================================

/**
 * Debug keyword matching for a specific text and category
 */
export function debugKeywordMatching(text: string, category: ReleaseCategory): {
  originalText: string
  normalizedText: string
  keywords: readonly string[]
  matches: string[]
  confidence: number
  details: Array<{ keyword: string; matched: boolean; reason: string }>
} {
  const originalText = text
  const normalizedText = normalizeText(text)
  const keywords = RELEASE_KEYWORDS[category]
  const matches = findMatchingKeywords(text, category)
  const confidence = calculateMatchConfidence(matches, keywords.length, text.length)
  
  const details = keywords.map(keyword => {
    const matched = matchesKeyword(normalizedText, keyword)
    const reason = matched 
      ? `Found "${keyword}" in normalized text`
      : `"${keyword}" not found in normalized text`
    
    return { keyword, matched, reason }
  })
  
  return {
    originalText,
    normalizedText,
    keywords,
    matches,
    confidence,
    details
  }
}
